/**
 * 批量调账弹窗（Alice 的 openBatchAdjust）：粘贴多行文本，先校验预览，再整批执行。
 *
 * 背景：客服和运营要给一批用户补偿或扣回花瓣。每行格式「账号 ID 或邮箱, 金额, 事由」，金额单位花瓣，负数为扣减。
 * 设计意图：
 *   - 分两步：「校验并预览」只算不写（dry_run），列出每行的调整前后余额与问题；有错误的行时整批不能执行。
 *   - 请求号 ref 在弹窗里生成一次，预览不用、执行时带上；执行失败重试沿用同一个 ref，后端按「ref + 行号」幂等，
 *     已成功的行不会重复记账。执行成功后换新 ref，避免同一批文本再点一次被当成重试。
 *   - 文本或「整批赠送」勾选一变就作废预览，必须重新校验，保证执行的就是看过的内容。
 *   - 含超过 1 万花瓣的有偿余额发放时，要勾选确认才能执行（后端同样会校验）。
 * 关键约束：只有 admin 能打开（页面按钮对 support 隐藏）；一批最多 500 行（后端校验）。
 */
import { useEffect, useState } from 'react'
import { usersApi, type BatchResult } from '@ap/api-client/admin-users'
import { BATCH } from '@ap/i18n/admin-users'
import { useToast } from '@ap/ui'
import { Check, UModal, newRef, useU } from '../kit-users.tsx'

export function BatchAdjustModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const { t, G, err, num, tag } = useU()
  const B = G.users.batch
  const toast = useToast()
  const [text, setText] = useState('')
  const [asBound, setAsBound] = useState(false)
  const [res, setRes] = useState<BatchResult | null>(null)
  const [confirmLarge, setConfirmLarge] = useState(false)
  const [ref, setRef] = useState(newRef)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setText('')
      setAsBound(false)
      setRes(null)
      setConfirmLarge(false)
      setRef(newRef())
      setError('')
    }
  }, [open])

  const invalidate = () => {
    setRes(null)
    setConfirmLarge(false)
    setError('')
  }

  const run = async (dry: boolean) => {
    if (!text.trim()) {
      setError(t(B.empty))
      return
    }
    setBusy(true)
    setError('')
    try {
      const r = await usersApi.batchAdjust({
        text,
        dry_run: dry,
        as_bound: asBound,
        confirm_large_normal_grant: confirmLarge,
        ...(dry ? {} : { ref }),
      })
      if (dry) {
        setRes(r)
      } else {
        toast.ok(t(B.done, { n: r.summary.ok, net: r.summary.net_petals.toLocaleString(tag) }))
        setRef(newRef())
        onDone()
        onClose()
      }
    } catch (e) {
      console.error('[g2] 批量调账失败', e)
      setError(err(e))
    } finally {
      setBusy(false)
    }
  }

  const s = res?.summary
  const canRun = !!res && !!s && s.errors === 0 && s.ok > 0 && (s.large_normal_grants === 0 || confirmLarge)
  const rowText = (code: string) => {
    const e = BATCH[code]
    return e ? t(e) : code
  }

  return (
    <UModal
      open={open}
      title={t(B.title)}
      onClose={onClose}
      busy={busy}
      width={760}
      footer={
        <>
          <button className="btn btn-secondary" disabled={busy} onClick={onClose}>
            {t(G.common.cancel)}
          </button>
          <button className="btn btn-secondary" disabled={busy} onClick={() => run(true)}>
            {t(B.preview)}
          </button>
          <button className="btn btn-primary" disabled={busy || !canRun} onClick={() => run(false)}>
            {t(B.execute)}
          </button>
        </>
      }
    >
      <div className="u2-note" style={{ marginTop: 0, marginBottom: 6 }}>
        {t(B.format)}
        <br />
        {t(B.formatHint)}
      </div>
      <textarea
        className="u2-textarea"
        spellCheck={false}
        placeholder={t(B.placeholder)}
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          invalidate()
        }}
      />
      <div style={{ margin: '8px 0' }}>
        <Check
          checked={asBound}
          onChange={(v) => {
            setAsBound(v)
            invalidate()
          }}
          hint={t(B.asBoundHint)}
        >
          {t(B.asBound)}
        </Check>
      </div>
      {error && <div className="ops-note" style={{ color: 'var(--error-text)', marginTop: 8 }}>{error}</div>}
      {res && s && (
        <>
          <div className="ops-note" style={{ marginTop: 8 }}>
            {t(B.summary, {
              ok: s.ok,
              errors: s.errors,
              users: s.user_count,
              add: s.add_petals.toLocaleString(tag),
              sub: s.sub_petals.toLocaleString(tag),
              net: s.net_petals.toLocaleString(tag),
            })}
            {s.warnings > 0 && <div style={{ color: 'var(--warning-text)' }}>{t(B.warnings, { n: s.warnings })}</div>}
          </div>
          {s.large_normal_grants > 0 && (
            <div style={{ margin: '8px 0' }}>
              <Check checked={confirmLarge} onChange={setConfirmLarge}>
                {t(B.largeConfirm, { n: s.large_normal_grants })} {t(B.largeCheck)}
              </Check>
            </div>
          )}
          <div className="user-detail-table-wrap" style={{ maxHeight: 280, overflow: 'auto', fontSize: 12 }}>
            <table>
              <thead>
                <tr>
                  <th>{t(B.colLine)}</th>
                  <th>{t(B.colTarget)}</th>
                  <th>{t(B.colAmount)}</th>
                  <th>{t(B.colBefore)}</th>
                  <th>{t(B.colAfter)}</th>
                  <th>{t(B.colResult)}</th>
                </tr>
              </thead>
              <tbody>
                {res.items.map((it) => (
                  <tr key={it.line} className={it.error ? 'u2-err-row' : it.warning ? 'u2-warn-row' : undefined}>
                    <td>{it.line}</td>
                    <td style={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.raw}>
                      {it.label || it.target}
                    </td>
                    <td>{it.error ? '—' : it.amount_petals.toLocaleString(tag)}</td>
                    <td>{it.balance_before_units === undefined ? '—' : num(it.balance_before_units)}</td>
                    <td>{it.balance_after_units === undefined ? '—' : num(it.balance_after_units)}</td>
                    <td style={{ color: it.error ? 'var(--error-text)' : it.warning ? 'var(--warning-text)' : 'var(--success-text)' }}>
                      {it.error ? rowText(it.error) : it.warning ? rowText(it.warning) : t(B.ok)}
                      {it.note && !it.error && <div className="u2-note" style={{ marginTop: 2 }}>{it.note}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </UModal>
  )
}
